import { joinURL } from 'ufo';
import type {
	ImageModifiers,
	OperationGeneratorConfig,
	ProviderGetImage
} from '@nuxt/image';
import { validateSpecialCharacters } from '@/utils/index';
export function createMapper(map: any) {
	return (key?: string) => {
		return key ? map[key] || key : map.missingValue;
	};
}

// Esse método é uma cópia do método original do @nuxt/image. Estavamos tendo problema para usar o original, pois, ele vinha com coisas client-side e estamos usando no servidor também aqui.
export function createOperationsGenerator({
	formatter,
	keyMap,
	joinWith = '/',
	valueMap
}: OperationGeneratorConfig = {}) {
	if (!formatter) {
		formatter = (key, value) => `${key}=${value}`;
	}
	if (keyMap && typeof keyMap !== 'function') {
		keyMap = createMapper(keyMap);
	}
	const map = valueMap || {};
	Object.keys(map).forEach(valueKey => {
		if (typeof map[valueKey] !== 'function') {
			map[valueKey] = createMapper(map[valueKey]);
		}
	});

	return (modifiers: { [key: string]: string } = {}) => {
		const operations = Object.entries(modifiers)
			.filter(([_, value]) => typeof value !== 'undefined')
			.map(([key, value]) => {
				const mapper = map[key];
				if (typeof mapper === 'function') {
					value = mapper(modifiers[key]!);
				}

				key = typeof keyMap === 'function' ? keyMap(key) : key;

				return formatter!(key, value);
			});

		return operations.join(joinWith);
	};
}

interface S3Bucket {
	name: string;
	url: string;
}

interface ResizeOptions {
	width?: number;
	height?: number;
}

interface ImageEdits {
	format?: string;
	quality?: number;
	blur?: number;
	resize?: ResizeOptions;
	rotate?: null;
	overlayWith?: {
		bucket: string;
		key: string;
		wRatio: number;
		hRatio: number;
		alpha: number;
	};
}

interface ImageConfig {
	bucket: string;
	key: string;
	edits: ImageEdits;
}

const S3_BUCKETS: S3Bucket[] = [
	{
		name: 'blintz-properties-sandbox',
		url: 'https://blintz-properties-sandbox.s3.amazonaws.com/'
	},
	{
		name: 'blintz-properties-sandbox',
		url: 'https://blintz-properties-sandbox.s3.us-east-2.amazonaws.com/'
	},
	{
		name: 'pilar-company-assets',
		url: 'https://pilar-company-assets.s3.amazonaws.com/'
	},
	{
		name: 'pilar-company-assets',
		url: 'https://pilar-company-assets.s3.us-east-2.amazonaws.com/'
	},
	{
		name: 'static.pilarhomes.com.br',
		url: 'https://static.pilarhomes.com.br/'
	},
	{
		name: 'static-staging.pilarhomes.com.br',
		url: 'https://static-staging.pilarhomes.com.br/'
	}
];

const LAMBDA_BASE_URL: string =
	process.env.NUXT_AWS_SERVERLESS_IMAGE_HANDLER_URL ||
	'https://imagens.pilarhomes.com.br';

const cloudfrontAvailableKeys = [
	'format',
	'quality',
	'blur',
	'width',
	'height',
	'overlayWith'
] as const;
const cloudfrontAvailableFormats = ['webp', 'png', 'jpg'] as const;
const numberValueKeys = ['quality', 'blur'] as const;

const operationsGenerator = createOperationsGenerator();

function getWatermarkedImageConfig(imageUrl: string): ImageConfig | null {
	const imageOptions = imageUrl.replace(LAMBDA_BASE_URL, '');

	const stringEdits = imageOptions.split('/')?.pop();

	if (!stringEdits) return null;

	const config = JSON.parse(atob(stringEdits));

	return {
		...(config || {}),
		edits: {
			...(config?.edits || {})
		}
	};
}

const isPlaceholder = ({
	modifiers,
	edits
}: {
	modifiers: { [key: string]: string };
	edits: ImageEdits;
}) => {
	return modifiers.blur || edits.blur;
};

function getModifiersWithMergedOverlay(
	modifiers: Partial<ImageModifiers>,
	newOverlayWith: ImageModifiers['overlayWith']
) {
	if (!newOverlayWith) return modifiers;

	return {
		...modifiers,
		overlayWith: {
			...modifiers.overlayWith,
			...newOverlayWith
		}
	};
}

// eslint-disable-next-line complexity
export const getImage: ProviderGetImage = (
	src,
	{ modifiers = {}, baseUrl } = {}
) => {
	try {
		const imageUrl = src.trim();

		if (imageUrl.endsWith('.svg') || imageUrl.includes('localhost')) {
			return { url: imageUrl };
		}

		// const { hasSpecialCharacters } = validateSpecialCharacters(imageUrl);

		// if (hasSpecialCharacters) {
		// 	return { url: imageUrl };
		// }

		const isLambdaUrl = imageUrl.startsWith(LAMBDA_BASE_URL);

		const imageConfig = isLambdaUrl
			? getWatermarkedImageConfig(imageUrl)
			: null;

		const bucketIdentifier = isLambdaUrl ? imageConfig?.bucket : imageUrl;

		const bucket = S3_BUCKETS.find(
			({ url, name }) =>
				bucketIdentifier?.startsWith(url) || bucketIdentifier === name
		);

		if (!bucket) return { url: imageUrl };

		const imagePath = isLambdaUrl
			? imageConfig?.key
			: imageUrl.replace(bucket.url, '');

		if (!imagePath) return { url: imageUrl };

		// * O overlayWith do modifiers tem prioridade sobre o overlayWith do imageConfig
		const mergedModifiers = getModifiersWithMergedOverlay(
			modifiers,
			modifiers.overlayWith || imageConfig?.edits?.overlayWith
		);

		if (isLambdaUrl) {
			const originalImageUrl = joinURL(bucket.url, imagePath);

			return getImage(
				originalImageUrl,
				{
					modifiers: mergedModifiers,
					baseUrl
				},
				{} as any
			);
		}

		const operations = operationsGenerator(modifiers).split('/');
		const edits: ImageEdits = imageConfig?.edits || {};

		for (const operation of operations) {
			const [key, value] = operation.split('=');

			if (
				!value ||
				!cloudfrontAvailableKeys.includes(
					key as (typeof cloudfrontAvailableKeys)[number]
				)
			) {
				continue;
			}
			if (key === 'width' || key === 'height') {
				const dimensionKey = key as keyof ResizeOptions;
				edits.resize ||= {};
				edits.resize[dimensionKey] = +value;
			} else if (key === 'format') {
				if (
					!cloudfrontAvailableFormats.includes(
						value as (typeof cloudfrontAvailableFormats)[number]
					)
				) {
					continue;
				}

				edits.format = value;
			} else if (
				numberValueKeys.includes(key as (typeof numberValueKeys)[number])
			) {
				const numericKey = key as 'quality' | 'blur';
				edits[numericKey] = +value;
			} else if (key === 'overlayWith') {
				edits.overlayWith = edits.overlayWith ?? modifiers.overlayWith;
			} else {
				// @ts-ignore
				edits[key as keyof ImageEdits] = value;
			}
		}

		edits.format ||= 'webp';
		edits.rotate ||= null; // Evita o bug de inversão de imagem em proporção vertical (https://github.com/aws-solutions/serverless-image-handler/issues/540)

		if (!Object.keys(edits).length) return { url: imageUrl };

		if (edits.overlayWith && isPlaceholder({ modifiers, edits })) {
			delete edits.overlayWith;
		}

		const imageRequest = JSON.stringify({
			bucket: bucket.name,
			key: imagePath,
			edits
		});

		const encodedImageRequest = btoa(imageRequest);

		return {
			url: joinURL(baseUrl, encodedImageRequest)
		};
	} catch (error) {
		console.error(error);
		// Sentry.captureException(error, {
		// 	originalException: error,
		// 	data: {
		// 		message: 'Error while getting image url using cloudfront provider.',
		// 		source: 'cloudfrontImageProvider.getImage',
		// 		imageSource: src.trim(),
		// 		imageBaseUrl: baseUrl,
		// 		modifiers: isEmpty(modifiers) ? null : modifiers
		// 	}
		// });
		return { url: src };
	}
};
